import { useState } from "react";
import { callFunction } from "../api/callFunction";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { capture, EVENTS } from "../analytics";

const INPUT =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const LABEL = "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1";
// INPUT's shape with its own background, so a read-only field reads as inert.
// Spelled out rather than composed from INPUT: both would set a dark background,
// and which one wins is decided by their order in the compiled stylesheet, not
// by the order they are written here.
const READONLY_FIELD =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 dark:text-gray-100 px-3 py-2 text-sm cursor-default truncate";

// `lockedEventID` pins the coupon to one event (set when opened from an event's
// Coupons section): the scope/event pickers are hidden and the event is fixed.
export default function CouponForm({ events, defaultEventID, lockedEventID, isSuperAdmin, onClose, onCreated }) {
  const locked = !!lockedEventID;
  const [code, setCode] = useState("");
  // "single" (one event), "multi" (several events) or "global" (every event,
  // including ones not created yet). The last two are super admins only.
  const [scope, setScope] = useState("single");
  const [eventID, setEventID] = useState(lockedEventID || defaultEventID || "");
  const [eventIDs, setEventIDs] = useState(defaultEventID ? [defaultEventID] : []);
  const [eventSearch, setEventSearch] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [percentageOff, setPercentageOff] = useState(10);
  const [maxUses, setMaxUses] = useState(10);
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const toggleEvent = (id) =>
    setEventIDs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const filteredEvents = eventSearch.trim()
    ? events.filter((e) => (e.name || "").toLowerCase().includes(eventSearch.trim().toLowerCase()))
    : events;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (!normalized) return toast.error("Code is required");
    const multi = scope === "multi";
    const global = scope === "global";
    if (multi && eventIDs.length === 0) return toast.error("Pick at least one event");
    if (!multi && !global && !eventID) return toast.error("Pick an event");
    if (discountType === "percentage" && (percentageOff < 1 || percentageOff > 100)) {
      return toast.error("Percentage must be 1-100");
    }
    if (!maxUses || maxUses < 1) return toast.error("Max uses must be at least 1");

    setSubmitting(true);
    try {
      await callFunction("adminCreateCoupon", {
        code: normalized,
        ...(global ? { allEvents: true } : multi ? { eventIDs } : { eventID }),
        discountType,
        percentageOff: discountType === "percentage" ? Number(percentageOff) : undefined,
        maxUses: Number(maxUses),
        active,
      });
      // After the callable resolves, so this counts coupons that exist. The
      // CODE is never sent — `code` is on the sanitizer's key blocklist (it is
      // there for OTPs), and it would be dropped even if it were passed.
      capture(EVENTS.COUPON_CREATED, {
        // Which surface made it. /coupons is operator-only and routes.js keeps it
        // out of HOST_ROUTES, so only creates from the event panel belong in a
        // host metric. Stated rather than inferred from $pathname, which a route
        // rename would break silently.
        source: locked ? "event_panel" : "coupons_page",
        scope,
        // A global coupon has no count: it covers events that do not exist yet.
        eventCount: global ? null : multi ? eventIDs.length : 1,
        discountType,
        percentageOff: discountType === "percentage" ? Number(percentageOff) : null,
        maxUses: Number(maxUses),
        activeOnCreate: active,
      });
      toast.success("Coupon created");
      onCreated();
    } catch (err) {
      toast.error(err.message || "Failed to create coupon");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-xl">
          <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New coupon</h2>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <label className={LABEL}>Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onBlur={(e) => setCode(e.target.value.trim().toUpperCase())}
                placeholder="SUMMER50"
                className={`${INPUT} font-mono uppercase`}
                autoFocus
              />
            </div>

            {isSuperAdmin && !locked && (
              <div>
                <label className={LABEL}>Applies to</label>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value="single"
                      checked={scope === "single"}
                      onChange={() => setScope("single")}
                    />
                    One event
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value="multi"
                      checked={scope === "multi"}
                      onChange={() => setScope("multi")}
                    />
                    Multiple events
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value="global"
                      checked={scope === "global"}
                      onChange={() => setScope("global")}
                    />
                    All events
                  </label>
                </div>
              </div>
            )}

            {locked ? (
              <div>
                <label className={LABEL}>Event</label>
                <div className={READONLY_FIELD}>
                  {events.find((e) => e.id === lockedEventID)?.name || "This event"}
                </div>
              </div>
            ) : scope === "global" ? (
              <p className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                Works on every event, including ones created later. Each person
                can use it once. Only a super admin can change or delete it, and
                no other coupon can reuse this code while it is active.
              </p>
            ) : scope === "single" ? (
              <div>
                <label className={LABEL}>Event</label>
                <select
                  value={eventID}
                  onChange={(e) => setEventID(e.target.value)}
                  className={INPUT}
                >
                  <option value="">— pick an event —</option>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}{typeof e.cost === "number" && e.cost > 0 ? ` ($${e.cost})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className={LABEL}>
                  Events
                  {eventIDs.length > 0 && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                      ({eventIDs.length} selected)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder="Search events…"
                  className={`${INPUT} mb-2`}
                />
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-300 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredEvents.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      No events found.
                    </div>
                  ) : (
                    filteredEvents.map((e) => (
                      <label
                        key={e.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={eventIDs.includes(e.id)}
                          onChange={() => toggleEvent(e.id)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary"
                        />
                        <span className="truncate">
                          {e.name}
                          {typeof e.cost === "number" && e.cost > 0 ? ` ($${e.cost})` : ""}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            <div>
              <label className={LABEL}>Discount type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="discountType"
                    value="percentage"
                    checked={discountType === "percentage"}
                    onChange={() => setDiscountType("percentage")}
                  />
                  Percentage off
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="discountType"
                    value="free"
                    checked={discountType === "free"}
                    onChange={() => setDiscountType("free")}
                  />
                  Free (100% off)
                </label>
              </div>
            </div>

            {discountType === "percentage" && (
              <div>
                <label className={LABEL}>Percentage off</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={percentageOff}
                  onChange={(e) => setPercentageOff(e.target.value)}
                  className={INPUT}
                />
              </div>
            )}

            <div>
              <label className={LABEL}>Max uses</label>
              <input
                type="number"
                min={1}
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className={INPUT}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="active"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary"
              />
              <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-200">
                Active
              </label>
            </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 dark:border-gray-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primaryDark rounded-md disabled:opacity-50"
              >
                {submitting ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
